<script lang="ts">
  import Widget from '../widgets/index.svelte'
  import { detectFieldType, type FieldType } from '../widgets/detect'

  type Entry = {
    key: string
    value: unknown
  }

  type ParsedState =
    | {
        ok: true
        value: Record<string, unknown>
      }
    | {
        ok: false
        error: string
      }

  const titleFieldPriority = ['name', 'title', 'id', 'type']
  const widgetTypes = new Set<FieldType>(['date', 'email', 'url', 'phone', 'status', 'bool', 'null', 'id', 'count'])

  let {
    value,
  }: {
    value: unknown
  } = $props()

  const parsedState = $derived(parseValue(value))
  const record = $derived(parsedState.ok ? parsedState.value : undefined)
  const titleEntry = $derived(record ? getTitleEntry(record) : undefined)
  const scalarEntries = $derived(record ? getScalarEntries(record) : [])
  const sectionEntries = $derived(record ? getSectionEntries(record) : [])

  function parseValue(input: unknown): ParsedState {
    if (typeof input === 'string') {
      try {
        const parsed = JSON.parse(input)

        if (isPlainObject(parsed)) {
          return { ok: true, value: parsed }
        }

        return { ok: false, error: 'Record View expects a JSON object' }
      } catch (error) {
        return {
          ok: false,
          error: error instanceof Error ? error.message : 'Unable to parse JSON',
        }
      }
    }

    if (isPlainObject(input)) {
      return { ok: true, value: input }
    }

    return { ok: false, error: 'Record View expects a JSON object' }
  }

  function isPlainObject(input: unknown): input is Record<string, unknown> {
    return typeof input === 'object' && input !== null && !Array.isArray(input)
  }

  function isScalar(input: unknown): boolean {
    return input === null || ['string', 'number', 'boolean'].includes(typeof input)
  }

  function getScalarEntries(input: Record<string, unknown>): Entry[] {
    return Object.entries(input)
      .filter(([, entryValue]) => isScalar(entryValue))
      .map(([key, entryValue]) => ({ key, value: entryValue }))
  }

  function getSectionEntries(input: Record<string, unknown>): Entry[] {
    return Object.entries(input)
      .filter(([, entryValue]) => !isScalar(entryValue))
      .map(([key, entryValue]) => ({ key, value: entryValue }))
  }

  function getTitleEntry(input: Record<string, unknown>): Entry | undefined {
    for (const field of titleFieldPriority) {
      const match = Object.entries(input).find(([key, entryValue]) => {
        return key.toLowerCase() === field && isScalar(entryValue) && entryValue !== null
      })

      if (match) {
        return { key: match[0], value: match[1] }
      }
    }

    return getScalarEntries(input).find((entry) => entry.value !== null)
  }

  function labelize(key: string): string {
    return key
      .replaceAll('_', ' ')
      .replaceAll('-', ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase())
  }

  function getArrayObjectKeys(items: unknown[]): string[] {
    const keys = new Set<string>()

    for (const item of items) {
      if (!isPlainObject(item)) {
        continue
      }

      for (const key of Object.keys(item)) {
        keys.add(key)
      }
    }

    return Array.from(keys).slice(0, 6)
  }

  function formatPrimitive(input: unknown): string {
    if (input === null) {
      return 'Not provided'
    }

    if (typeof input === 'boolean') {
      return input ? 'Yes' : 'No'
    }

    return String(input)
  }
</script>

{#snippet fieldValue(entry: Entry)}
  {@const fieldType = detectFieldType(entry.key, entry.value)}
  {#if widgetTypes.has(fieldType)}
    <Widget type={fieldType} value={entry.value} />
  {:else if typeof entry.value === 'number'}
    <span class="record-value record-value-number">{entry.value}</span>
  {:else}
    <span class="record-value">{formatPrimitive(entry.value)}</span>
  {/if}
{/snippet}

{#snippet scalarChips(items: unknown[])}
  <div class="record-chip-list">
    {#each items as item, index (`${index}-${String(item)}`)}
      <span class="record-chip">{formatPrimitive(item)}</span>
    {/each}
  </div>
{/snippet}

{#if !parsedState.ok}
  <div class="record-view">
    <section class="record-card">
      <h2>Invalid record</h2>
      <p class="record-error">{parsedState.error}</p>
    </section>
  </div>
{:else if record}
  <div class="record-view">
    <header class="record-header">
      <span class="record-eyebrow">Record</span>
      <h2>{titleEntry ? formatPrimitive(titleEntry.value) : 'Untitled record'}</h2>
      {#if titleEntry}
        <span class="record-title-key">{labelize(titleEntry.key)}</span>
      {/if}
    </header>

    <div class="record-metadata-strip" aria-label="Record metadata">
      {#each scalarEntries as entry (entry.key)}
        <div class="record-metadata-chip">
          <span>{labelize(entry.key)}</span>
          {@render fieldValue(entry)}
        </div>
      {/each}
    </div>

    <div class="record-sections">
      {#each sectionEntries as section (section.key)}
        <section class="record-card">
          <h3>{labelize(section.key)}</h3>

          {#if isPlainObject(section.value)}
            <div class="record-field-grid">
              {#each Object.entries(section.value) as [key, entryValue] (key)}
                <div class="record-field-row">
                  <span class="record-field-label">{labelize(key)}</span>
                  {@render fieldValue({ key, value: entryValue })}
                </div>
              {/each}
            </div>
          {:else if Array.isArray(section.value) && section.value.every(isPlainObject)}
            {@const tableKeys = getArrayObjectKeys(section.value)}
            <div class="record-table-wrap">
              <table class="record-table">
                <thead>
                  <tr>
                    {#each tableKeys as key (key)}
                      <th>{labelize(key)}</th>
                    {/each}
                  </tr>
                </thead>
                <tbody>
                  {#each section.value as item, rowIndex (`${section.key}-${rowIndex}`)}
                    {#if isPlainObject(item)}
                      <tr>
                        {#each tableKeys as key (key)}
                          <td>{@render fieldValue({ key, value: item[key] ?? null })}</td>
                        {/each}
                      </tr>
                    {/if}
                  {/each}
                </tbody>
              </table>
            </div>
          {:else if Array.isArray(section.value) && section.value.every(isScalar)}
            {@render scalarChips(section.value)}
          {:else if Array.isArray(section.value)}
            <div class="record-list">
              {#each section.value as item, index (`${section.key}-${index}`)}
                <div class="record-list-item">
                  {#if isPlainObject(item)}
                    <div class="record-field-grid">
                      {#each Object.entries(item) as [key, entryValue] (key)}
                        <div class="record-field-row">
                          <span class="record-field-label">{labelize(key)}</span>
                          {@render fieldValue({ key, value: entryValue })}
                        </div>
                      {/each}
                    </div>
                  {:else if Array.isArray(item) && item.every(isScalar)}
                    {@render scalarChips(item)}
                  {:else}
                    <span class="record-value">{JSON.stringify(item)}</span>
                  {/if}
                </div>
              {/each}
            </div>
          {:else}
            <span class="record-value">{JSON.stringify(section.value)}</span>
          {/if}
        </section>
      {/each}
    </div>
  </div>
{/if}
